import { motion } from "framer-motion";
import Button from "../../../components/common/Button";
import { useFormDataStore } from "../../../store/formDataStore";
import { StepProps } from "../../../types/onboarding";
import styles from "../OnboardingPage.module.css";
import { createProfile, fetchProfiles, fetchNatalChart } from "../../../services/api/profile";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import Preloader from "../../../components/common/Preloader";
import { useNatalChartStore } from "../../../store/natalChartStore";
import StepError from "../../../components/common/StepError";
import { getUserFriendlyError } from "../../../utils/errorMessages";
import type { CreateProfilePayload } from "../../../services/api/profile";
import { useProfileStore } from "../../../store/profileStore";
import type { Profile } from "../../../store/profileStore"; // Используем твой тип Profile

const StepGender: React.FC<StepProps> = ({ onBack }) => {
  const { formData } = useFormDataStore();
  const setStep = useFormDataStore((s) => s.setStep);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const setNatalChart = useNatalChartStore((state) => state.setNatalChart);
  const setProfile = useProfileStore((state) => state.setProfile); // Сохраняем профиль

  const buttonRef = useRef<HTMLButtonElement>(null);

  // 🔸 Автоскролл к кнопке "Готово"
  useEffect(() => {
    if (formData.gender) {
      setTimeout(() => {
        buttonRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [formData.gender]);

  // 🔒 Блокируем скролл при загрузке
  useEffect(() => {
    if (loading) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [loading]);

  const handleComplete = async () => {
    const payload: CreateProfilePayload = {
      name: formData.name,
      dob: `${formData.birthDate}T${formData.birthTime}:00.000Z`,
      sex: formData.gender === "Мужской" ? "male" : "female",
      city: formData.cityNameForApi || formData.placeOfBirth,
      country_code: formData.countryCode || "RU",
      timezone: formData.timezone || "Europe/Moscow",
      latitude: formData.latitude ?? 0,
      longitude: formData.longitude ?? 0,
      is_main: true,
      forecast: 0,
    };

    setErrorMessage(null);
    setLoading(true);

    try {
      await createProfile(payload);

      await new Promise((res) => setTimeout(res, 2000));

      for (let i = 0; i < 10; i++) {
        const profiles = await fetchProfiles();
        const main = profiles.find((p: Profile) => p.is_main);

        if (main && Number(main.forecast) === 1) {
          setProfile(main); // Сохраняем профиль в Zustand

          const chart = await fetchNatalChart();

          if (chart?.natalChart?.summary) {
            setNatalChart(chart.natalChart);
            setStep(-1);
            navigate("/natal-chart");
          } else {
            throw new Error("Натальная карта не содержит summary");
          }
          return;
        }

        await new Promise((r) => setTimeout(r, 2000));
      }

      throw new Error("Прогноз не готов: превышено время ожидания");
    } catch (err: unknown) {
      console.error("❌ Ошибка при создании профиля или получении карты:", err);
      setErrorMessage(getUserFriendlyError(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading)
    return (
      <div style={{ marginTop: "-100px" }}>
        <Preloader />
      </div>
    );

  if (errorMessage) return <StepError message={errorMessage} onRetry={handleComplete} />;

  return (
    <motion.div
      key="step-gender"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
    >
      <h2>Ваш пол</h2>
      <p>Это поможет сбалансировать мужскую и женскую энергию.</p>

      <div className={styles["radio-group"]}>
        {["Мужской", "Женский"].map((g) => (
          <label key={g}>
            <input
              type="radio"
              name="gender"
              value={g}
              checked={formData.gender === g}
              onChange={(e) => useFormDataStore.getState().setFormData({ gender: e.target.value })}
            />
            {g}
          </label>
        ))}
      </div>

      <div className={`${styles["button-row"]} ${styles["two-buttons"]}`}>
        <Button onClick={onBack}>Назад</Button>
        <Button ref={buttonRef} onClick={handleComplete} disabled={!formData.gender}>
          Готово
        </Button>
      </div>
    </motion.div>
  );
};

export default StepGender;
